import { Quaternion } from '@babylonjs/core/Maths/math.vector';

import { TARGET_SIZE } from '../room/ModelLibrary';

/**
 * Grid snapping — client-side only, first iteration (plan/grid.md). The owner's framing:
 * *"pure client work for the first iteration. If I like how it feels we can add backend
 * enforcement later."* So this is entirely best-effort: nothing here is enforced by the
 * server, the cell size is not in `Tunet.Contracts`, and two peers can still, in principle,
 * round to the same cell in the same instant. `plan/grid.md`'s own "Enforcement has to be
 * server-side" section is exactly this gap, left open on purpose — do not read the presence
 * of this file as that work being done.
 *
 * Pure numbers in, pure numbers out — no Scene, no Mesh, nothing Babylon has to render,
 * the same split `WhiteboardLayout.ts`/`FadeAnimation.ts`/`AssistantLightAnimation.ts`
 * already use so this is testable without a headless renderer. `Vector3`/`Quaternion` are
 * used only as plain math types here, the same way `WhiteboardLayout.ts` already does.
 */

// spec: SPEC-067
// spec: SPEC-068

/** The grid's cell size, in metres — `ModelLibrary.TARGET_SIZE`, unchanged. Two shapes in
 *  adjacent cells touch exactly at this size; any other spacing would leave gaps or
 *  overlaps. See `plan/grid.md`, "The grid is the node size". */
export const CELL_SIZE = TARGET_SIZE;

/**
 * How many cells outward `nearestFreeCell` will search before giving up — 5 cells, 1.5 m at
 * `CELL_SIZE`. `plan/grid.md` flagged this as the one thing it left undecided: "placing into
 * a dense corner must not fling a shape several metres away to find room." 1.5 m is close
 * enough that a shape which does get relocated still lands recognisably near where it was
 * placed, and far enough to find room in all but the most saturated corners of a room.
 */
export const DEFAULT_SEARCH_RADIUS_CELLS = 5;

export interface Point3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface CellIndex {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * Which cell `position` falls in, at `cellSize`. `Math.round`, not `Math.floor` — a cell is
 * centred on its own index times `cellSize`, so the boundary between two cells sits at the
 * halfway point between their centres, not at a multiple of `cellSize` itself. This is also
 * what makes negative coordinates behave: `Math.round(-0.1 / 0.3)` is `0`, the same answer a
 * symmetric positive input gets, where `Math.floor` would silently round every negative value
 * toward the wrong neighbour.
 */
export function cellIndexOf(position: Point3, cellSize: number): CellIndex {
  // `|| 0` folds a `-0` result (e.g. Math.round(-0.05 / 0.3)) back to `0` — mathematically the
  // same cell, but `Object.is(-0, 0)` is false, which would otherwise make a position on the
  // negative side of the origin compare unequal to the identical index computed from a
  // positive one, and make `cellCenterOf` hand back `-0` as a position component.
  return {
    x: Math.round(position.x / cellSize) || 0,
    y: Math.round(position.y / cellSize) || 0,
    z: Math.round(position.z / cellSize) || 0,
  };
}

/** The world-space centre of a cell, given its index. Inverse of `cellIndexOf` up to
 *  rounding. */
export function cellCenterOf(index: CellIndex, cellSize: number): Point3 {
  return { x: index.x * cellSize, y: index.y * cellSize, z: index.z * cellSize };
}

/** A stable string key for a cell index — what `occupied` sets below are keyed by, so a
 *  `Set<string>` is enough to test occupancy without carrying `CellIndex` objects around
 *  (which would need a custom equality check to be usable in a `Set` at all). */
export function cellKey(index: CellIndex): string {
  return `${index.x}:${index.y}:${index.z}`;
}

/** The cell key `position` rounds to at `cellSize` — `cellKey(cellIndexOf(...))` in one call,
 *  since every caller that wants a key from a raw position wants both steps together. */
export function cellKeyOf(position: Point3, cellSize: number): string {
  return cellKey(cellIndexOf(position, cellSize));
}

/**
 * The set of cell keys occupied by `positions`, at `cellSize` — one node's world position
 * becomes one cell key. Callers build this from `RoomState.nodes`, excluding whichever node
 * is being placed or released (its own prior cell would otherwise count as "occupied" against
 * itself).
 */
export function occupiedCellKeys(positions: Iterable<Point3>, cellSize: number): Set<string> {
  const keys = new Set<string>();

  for (const position of positions) {
    keys.add(cellKeyOf(position, cellSize));
  }

  return keys;
}

export interface NearestFreeCell {
  readonly index: CellIndex;
  readonly position: Point3;
}

/**
 * The nearest free cell to `desired`, or `null` if nothing is free within
 * `searchRadiusCells` cells. Searches outward from `desired`'s own cell in a bounded cube,
 * discarding anything past `searchRadiusCells` cells away by straight-line distance — a
 * cube search bounded only by a Chebyshev radius would call a far corner "within radius" when
 * it is farther, in a straight line, than a cell directly in front; this keeps "radius" honest.
 *
 * Returning `null` rather than the least-bad occupied option, or the nearest cell regardless
 * of how far it is, is deliberate — see `plan/grid.md`'s own "One thing still to decide":
 * placing into a dense corner must not fling a shape metres away to find room, and a bounded,
 * honest failure is the answer chosen. The caller decides what "failure" means for its own
 * gesture (a fresh placement vs. a release of an existing node) — this function only ever
 * answers "is there room, and if so where".
 */
export function nearestFreeCell(
  desired: Point3,
  occupied: ReadonlySet<string>,
  cellSize: number,
  searchRadiusCells: number,
): NearestFreeCell | null {
  const center = cellIndexOf(desired, cellSize);
  const radiusSq = searchRadiusCells * searchRadiusCells;

  let best: NearestFreeCell | null = null;
  let bestDistanceSq = Number.POSITIVE_INFINITY;

  for (let dx = -searchRadiusCells; dx <= searchRadiusCells; dx += 1) {
    for (let dy = -searchRadiusCells; dy <= searchRadiusCells; dy += 1) {
      for (let dz = -searchRadiusCells; dz <= searchRadiusCells; dz += 1) {
        if (dx * dx + dy * dy + dz * dz > radiusSq) {
          continue;
        }

        const index: CellIndex = { x: center.x + dx, y: center.y + dy, z: center.z + dz };

        if (occupied.has(cellKey(index))) {
          continue;
        }

        const position = cellCenterOf(index, cellSize);
        const distanceSq =
          (position.x - desired.x) ** 2 +
          (position.y - desired.y) ** 2 +
          (position.z - desired.z) ** 2;

        if (distanceSq < bestDistanceSq) {
          bestDistanceSq = distanceSq;
          best = { index, position };
        }
      }
    }
  }

  return best;
}

const QUARTER_TURN = Math.PI / 2;
const FULL_TURN = Math.PI * 2;

/**
 * Rounds `yawRadians` to the nearest 90°, wrapped into `(-π, π]` — a stable representative for
 * every equivalent angle (3.5 turns and 1.5 turns of the same direction snap to the same
 * answer), rather than an unbounded value that happens to be a multiple of 90° but drifts
 * further from zero the more a node has been spun around before release.
 */
export function snapYaw(yawRadians: number): number {
  const snapped = Math.round(yawRadians / QUARTER_TURN) * QUARTER_TURN;

  // Wrap into (-π, π]: shift into [0, 2π), reduce mod 2π, shift back. The final `|| FULL_TURN`
  // guard turns a wrapped 0 back into +π when the input was an exact odd multiple of π — mod
  // arithmetic alone would otherwise put -π and +π on opposite sides of the same physical
  // angle, which is the case the "wraps correctly" requirement exists to cover.
  const wrapped = ((((snapped + Math.PI) % FULL_TURN) + FULL_TURN) % FULL_TURN) - Math.PI;
  return wrapped === -Math.PI ? Math.PI : wrapped;
}

/**
 * The yaw component of `rotation`, in radians — `Quaternion.toEulerAngles().y` in this
 * project's own left-handed, Y-up convention (CLAUDE.md, "Coordinates"), the same extraction
 * Babylon itself provides rather than a hand-rolled formula tied to a different handedness.
 */
export function yawOf(rotation: Quaternion): number {
  return rotation.toEulerAngles().y;
}

/** An upright quaternion carrying only the given yaw — no pitch, no roll. Snapping to the
 *  grid is meant to read as "aligned and upright" (plan/grid.md: "Aligned positions with
 *  arbitrary rotation still reads as messy, and the rotation is the half that carries it"),
 *  not "whatever pitch and roll the hand happened to be at, with the yaw rounded off" — a
 *  shape released mid-tumble should land flat on the grid, not merely yaw-aligned while still
 *  tilted. */
export function uprightRotationAtYaw(yawRadians: number): Quaternion {
  return Quaternion.FromEulerAngles(0, yawRadians, 0);
}

export interface GridSnapResult {
  readonly position: Point3;
  readonly rotation: Quaternion;
  /** False when no free cell was found within the search radius — `position` is then the
   *  caller's own `desired` position, unsnapped, so a shape is never relocated arbitrarily far
   *  to force it onto the grid. The caller decides what to tell the user; see `nearestFreeCell`'s
   *  own doc comment. */
  readonly snappedToCell: boolean;
}

/**
 * The full snap for one shape: nearest free cell for position (or an honest miss), yaw rounded
 * to the nearest 90° regardless of whether a cell was found. `desiredRotation` only ever
 * contributes its yaw — see `uprightRotationAtYaw`.
 */
export function snapPlacement(
  desiredPosition: Point3,
  desiredRotation: Quaternion,
  occupied: ReadonlySet<string>,
  cellSize: number = CELL_SIZE,
  searchRadiusCells: number = DEFAULT_SEARCH_RADIUS_CELLS,
): GridSnapResult {
  const nearest = nearestFreeCell(desiredPosition, occupied, cellSize, searchRadiusCells);
  const rotation = uprightRotationAtYaw(snapYaw(yawOf(desiredRotation)));

  return nearest === null
    ? { position: desiredPosition, rotation, snappedToCell: false }
    : { position: nearest.position, rotation, snappedToCell: true };
}
